import { NavigationType } from 'react-router-dom';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AppRoutes } from '@/constants/routes';

const MAX_HISTORY_LENGTH = 50;

interface RouterHistoryState {
    readonly idx?: number;
}

function getBrowserHistoryIdx(): number {
    return (window.history.state as RouterHistoryState | null)?.idx ?? 0;
}

export function hasRouterHistory(): boolean {
    return getBrowserHistoryIdx() > 0;
}

function findMatchingIndex(
    history: NavigationHistoryEntry[],
    currentIndex: number,
    targetPath: string,
    searchBackward: boolean,
): number | null {
    if (searchBackward) {
        for (let index = currentIndex - 1; index >= 0; index--) {
            if (history[index]?.path === targetPath) {
                return index;
            }
        }
    } else {
        for (let index = currentIndex + 1; index < history.length; index++) {
            if (history[index]?.path === targetPath) {
                return index;
            }
        }
    }
    return null;
}

export interface NavigationHistoryEntry {
    readonly path: string;
    readonly route: AppRoutes | null;
    readonly timestamp: number;
}

interface NavigationHistoryState {
    readonly history: NavigationHistoryEntry[];
    readonly currentIndex: number;
    readonly lastBrowserHistoryIdx: number;
    readonly navigate: (
        entry: Omit<NavigationHistoryEntry, 'timestamp'>,
        navigationType: NavigationType,
    ) => void;
    readonly syncWithBrowser: (currentPath: string) => void;
    readonly clearHistory: () => void;
}

export const useNavigationHistoryStore = create<NavigationHistoryState>()(
    persist(
        (set) => ({
            history: [],
            currentIndex: -1,
            lastBrowserHistoryIdx: 0,

            navigate: (entry, navigationType) => {
                const currentBrowserIdx = getBrowserHistoryIdx();

                set((state) => {
                    const currentEntry = state.history[state.currentIndex];

                    if (currentEntry && currentEntry.path === entry.path) {
                        return {
                            lastBrowserHistoryIdx: currentBrowserIdx,
                            history: state.history.map((historyEntry, index) =>
                                index === state.currentIndex
                                    ? { ...historyEntry, route: entry.route }
                                    : historyEntry,
                            ),
                        };
                    }

                    if (navigationType === 'POP') {
                        const isGoingBack =
                            currentBrowserIdx < state.lastBrowserHistoryIdx;

                        const matchingIndex = findMatchingIndex(
                            state.history,
                            state.currentIndex,
                            entry.path,
                            isGoingBack,
                        );

                        if (matchingIndex !== null) {
                            return {
                                currentIndex: matchingIndex,
                                lastBrowserHistoryIdx: currentBrowserIdx,
                            };
                        }

                        const anyMatchingIndex = state.history.findIndex(
                            (historyEntry) => historyEntry.path === entry.path,
                        );

                        if (anyMatchingIndex !== -1) {
                            return {
                                currentIndex: anyMatchingIndex,
                                lastBrowserHistoryIdx: currentBrowserIdx,
                            };
                        }
                    }

                    if (navigationType === 'REPLACE') {
                        const newEntry: NavigationHistoryEntry = {
                            ...entry,
                            timestamp: Date.now(),
                        };

                        const newHistory =
                            state.currentIndex >= 0
                                ? [
                                      ...state.history.slice(
                                          0,
                                          state.currentIndex,
                                      ),
                                      newEntry,
                                      ...state.history.slice(
                                          state.currentIndex + 1,
                                      ),
                                  ]
                                : [newEntry];

                        return {
                            history: newHistory,
                            currentIndex: Math.max(0, state.currentIndex),
                            lastBrowserHistoryIdx: currentBrowserIdx,
                        };
                    }

                    const newEntry: NavigationHistoryEntry = {
                        ...entry,
                        timestamp: Date.now(),
                    };

                    const historyUpToCurrent = state.history.slice(
                        0,
                        state.currentIndex + 1,
                    );
                    const newHistory = [...historyUpToCurrent, newEntry].slice(
                        -MAX_HISTORY_LENGTH,
                    );

                    return {
                        history: newHistory,
                        currentIndex: newHistory.length - 1,
                        lastBrowserHistoryIdx: currentBrowserIdx,
                    };
                });
            },

            syncWithBrowser: (currentPath) => {
                const currentBrowserIdx = getBrowserHistoryIdx();

                set((state) => {
                    const currentEntry = state.history[state.currentIndex];

                    if (currentEntry?.path === currentPath) {
                        return { lastBrowserHistoryIdx: currentBrowserIdx };
                    }

                    const matchingIndex = state.history.findIndex(
                        (historyEntry) => historyEntry.path === currentPath,
                    );

                    if (matchingIndex !== -1) {
                        return {
                            currentIndex: matchingIndex,
                            lastBrowserHistoryIdx: currentBrowserIdx,
                        };
                    }

                    return {
                        history: [],
                        currentIndex: -1,
                        lastBrowserHistoryIdx: currentBrowserIdx,
                    };
                });
            },

            clearHistory: () => {
                set({
                    history: [],
                    currentIndex: -1,
                    lastBrowserHistoryIdx: 0,
                });
            },
        }),
        {
            name: 'navigation-history',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({
                history: state.history,
                currentIndex: state.currentIndex,
                lastBrowserHistoryIdx: state.lastBrowserHistoryIdx,
            }),
        },
    ),
);
